import type { PhotoProductsCartApi } from './PhotoProductsCartApi'
import { Cart } from './types/photoProductsCart'

/** An absent cartType is the server's default PHOTO_PRODUCTS cart: one server cart, one key. */
export const cartKey = (cartType: string | undefined) => cartType ?? 'PHOTO_PRODUCTS'

/** What the page knows about one server cart. `cart` is undefined until a response lands. */
export type CartEntry = {
  cart?: Cart | null
  /** A request for this cart is queued or in flight. */
  busy: boolean
  /** The last request failed and no server cart replaced what is shown. */
  stale: boolean
}

/** A cart as checkout sees it: a request in flight, the last one failed, or neither. */
export type CartState = 'busy' | 'stale' | 'settled'

export type CartRequest = (cartApi: PhotoProductsCartApi) => Promise<Cart | null>

/**
 * Every cart read and write, and the only path that installs a cart (sc-7811).
 * Requests run one at a time per server cart, each with a client fixed to that
 * cart, and a response installs under that cart's key only.
 *
 * - A failed write re-reads its cart before the cart settles: the server may
 *   have applied it.
 * - A failed read, or a failed re-read, marks the cart stale until a later
 *   response installs.
 */
export function createCartQueue({
  clientFor,
  update,
}: {
  clientFor: (cartType: string | undefined) => PhotoProductsCartApi
  update: (key: string, patch: Partial<CartEntry>) => void
}) {
  const tails = new Map<string, Promise<unknown>>()
  const pending = new Map<string, number>()

  const run = (
    cartType: string | undefined,
    request: CartRequest,
    { resyncOnFailure }: { resyncOnFailure: boolean }
  ): Promise<Cart | null> => {
    const key = cartKey(cartType)
    const cartApi = clientFor(cartType)
    const count = (pending.get(key) ?? 0) + 1
    pending.set(key, count)
    if (count === 1) {
      update(key, { busy: true })
    }
    const task = (tails.get(key) ?? Promise.resolve())
      .catch(() => undefined)
      .then(async () => {
        try {
          const cart = (await request(cartApi)) ?? null
          update(key, { cart, stale: false })
          return cart
        } catch (error) {
          try {
            if (!resyncOnFailure) {
              throw error
            }
            update(key, { cart: (await cartApi.getCart()) ?? null, stale: false })
          } catch {
            update(key, { stale: true })
          }
          throw error
        }
      })
      .finally(() => {
        const left = (pending.get(key) ?? 1) - 1
        pending.set(key, left)
        if (left === 0) {
          update(key, { busy: false })
        }
      })
    tails.set(key, task)
    return task
  }

  return {
    read: (cartType: string | undefined) =>
      run(cartType, (cartApi) => cartApi.getCart(), { resyncOnFailure: false }),
    write: (cartType: string | undefined, request: CartRequest) =>
      run(cartType, request, { resyncOnFailure: true }),
  }
}

export type CartStore = ReturnType<typeof createCartStore>

/**
 * The session's cart entries and their queue. It lives on the session's
 * `PhotoProductsCartClients`, so ordering survives a remount of any page that shows a cart.
 */
export function createCartStore(clientFor: (cartType: string | undefined) => PhotoProductsCartApi) {
  let entries: Record<string, CartEntry> = {}
  const listeners = new Set<() => void>()
  const queue = createCartQueue({
    clientFor,
    update: (key, patch) => {
      entries = { ...entries, [key]: { ...(entries[key] ?? { busy: false, stale: false }), ...patch } }
      listeners.forEach((listener) => listener())
    },
  })
  return {
    ...queue,
    getSnapshot: () => entries,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
